import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  EvRangeMapImagesModel,
  EvRangeMapImagesPaths,
  MAP_NOT_AVAILABLE,
} from 'client/data/models/ev-range-map-images';
import { ZipWithDefaultActionHandling } from 'site-modules/shared/components/zip-with-default-action-handling/zip-with-default-action-handling';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { Link } from 'site-modules/shared/components/link/link';
import { EVRangeMapImageUI } from 'site-modules/shared/components/ev-range-map-image/ev-range-map-image';
import { VisitorEntities } from 'client/data/models/visitor';

function ElectricityRangeUI({
  vehicleName = null,
  modelName = null,
  currentRange = null,
  rangeMapUrl = null,
  visitorLocation = null,
  isTestedRange = false,
  isEstimatedRange = false,
  isPluginHybrid = false,
  isPreview = false,
}) {
  const isMissingMapImage = rangeMapUrl && rangeMapUrl === MAP_NOT_AVAILABLE;
  const mapRangeMiles = rangeMapUrl && +get(rangeMapUrl.match(/\/(\d+)-miles\./), '[1]');
  const mapRangeMilesRoundTrip = mapRangeMiles && Math.ceil(mapRangeMiles / 50) * 25;

  const nonTestedRangeLabel = isPluginHybrid ? 'EPA Estimated Electric Range' : 'EPA Estimated Range';

  return (
    <div className="electricity-range">
      <div
        className={classnames({
          'heading-4 text-center': currentRange,
          'mb-0_25': currentRange && !isEstimatedRange,
          'mb-1': currentRange && isEstimatedRange,
        })}
      >
        {currentRange ? (
          <Fragment>
            {currentRange} miles
            {isEstimatedRange && (
              <PopoverItem iconClassName="medium" screenReaderTooltipLabel="estimated range">
                The range for a used {vehicleName} is estimated to be {currentRange} miles because electric cars
                typically experience 1-2% of range loss per year, with slightly faster degradation over the first 50,000
                miles as the car settles into its long term state, according to{' '}
                <Link
                  className="text-underline"
                  to="https://www.recurrentauto.com/research/lessons-in-electric-car-battery-health"
                  target="_blank"
                  rel="nofollow"
                >
                  Recurrent
                </Link>
                &apos;s study of 15,000 EVs.
              </PopoverItem>
            )}
          </Fragment>
        ) : (
          <Fragment>{isEstimatedRange ? 'Not available' : 'EPA Estimated Range not available'}</Fragment>
        )}
      </div>
      {!!currentRange && (
        <Fragment>
          <div className="medium text-center mb-1_5">
            {!isEstimatedRange && (
              <div className="mb-1">
                {isTestedRange ? (
                  <Fragment>
                    Edmunds Tested Range
                    <PopoverItem screenReaderTooltipLabel="Edmunds Tested Range">
                      Edmunds begins with a full battery charge and drives the vehicle on a mix of city and highway
                      roads (approximately 60% city, 40% highway) until the battery is almost entirely empty. (We target
                      10 miles of remaining range for safety.) The miles traveled and the indicated remaining range are
                      added together for the Edmunds Tested Range figure.
                    </PopoverItem>
                  </Fragment>
                ) : (
                  <Fragment>
                    {nonTestedRangeLabel}
                    <PopoverItem screenReaderTooltipLabel={nonTestedRangeLabel}>
                      Electric cars typically experience 1-2% of range loss per year with slightly faster degradation
                      over the first 50,000 miles as the car settles into its long term state, according to{' '}
                      <Link
                        className="text-underline"
                        to="https://www.recurrentauto.com/research/lessons-in-electric-car-battery-health"
                        target="_blank"
                        rel="nofollow"
                      >
                        Recurrent
                      </Link>
                      &apos;s study of 15,000 EVs.
                    </PopoverItem>
                  </Fragment>
                )}
              </div>
            )}
            {isPluginHybrid && (
              <div className="medium">
                The {modelName} will automatically switch to gas when the electric battery is low.
              </div>
            )}
          </div>
          {!isPreview && (
            <Fragment>
              <EVRangeMapImageUI url={rangeMapUrl} className="mb-1" />
              <div className="rounded-10 bg-cool-gray-100 py-1 px-0_5 text-center">
                {isMissingMapImage ? (
                  'No range map available for'
                ) : (
                  <Fragment>
                    Estimated range map
                    <PopoverItem screenReaderTooltipLabel="estimated range map">
                      This map is a visual representation of the possible one-way and round-trips by this vehicle (on a
                      full charge) from the geometric center of {get(visitorLocation, 'city')},{' '}
                      {get(visitorLocation, 'state')}. The depicted ranges are based on the estimated new vehicle range
                      value provided by the EPA, rounded down to {mapRangeMiles} miles for one-way and{' '}
                      {mapRangeMilesRoundTrip} miles for round-trip. Actual range will vary depending on the condition
                      of this vehicle’s battery pack, how you drive, driving conditions and other factors.
                      <div>
                        Map display generated using: ©&nbsp;
                        <Link to="openrouteservice.org" className="text-underline" rel="nofollow" target="_blank">
                          openrouteservice.org
                        </Link>{' '}
                        by HeiGIT | Map data ©&nbsp;
                        <Link
                          to="https://www.openstreetmap.org/#map=5/38.007/-95.844"
                          className="text-underline"
                          rel="nofollow"
                          target="_blank"
                        >
                          OpenStreetMap
                        </Link>{' '}
                        contributors |{' '}
                        <Link to="https://leafletjs.com/" className="text-underline" rel="nofollow" target="_blank">
                          Leaflet
                        </Link>
                      </div>
                    </PopoverItem>
                    from
                  </Fragment>
                )}{' '}
                <ZipWithDefaultActionHandling buttonClassName="size-16 fw-normal" />
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
    </div>
  );
}

ElectricityRangeUI.propTypes = {
  vehicleName: PropTypes.string,
  modelName: PropTypes.string,
  currentRange: PropTypes.number,
  rangeMapUrl: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  isTestedRange: PropTypes.bool,
  isEstimatedRange: PropTypes.bool,
  isPluginHybrid: PropTypes.bool,
  isPreview: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { rangeMapUrl }) => !rangeMapUrl;
export const ElectricityRange = connectToModel(React.memo(ElectricityRangeUI, propsAreEqual), {
  rangeMapUrl: bindToPath(
    ({ currentRange, isPreview }) => !isPreview && EvRangeMapImagesPaths.RANGE_MAP_CURRENT_CITY(currentRange),
    EvRangeMapImagesModel
  ),
});
